import {filter, includes, memoize} from "lodash";
import {RequestState} from "modules/enums";
import {getPredictionsByRoundId} from "modules/selectors/streak";
import {IStore} from "modules/types/store";
import {isUnitScheduled} from "modules/utils/match";
import {createSelector} from "reselect";

export const getUnitsStore = (store: IStore) => store.units;
export const getUnits = (store: IStore) => [...store.units.current, ...store.units.past];

export const getMatchesByRoundId = createSelector(
	[getUnits],
	(matches) => (roundId: number) => matches.filter((it) => it.roundId === roundId)
);

export const getMatchByIdSelector = createSelector([getUnits], (matches) =>
	memoize((id: number) => matches.find((it) => it.id === id))
);

export const getPlayerMatchesSelector = createSelector(
	[getMatchesByRoundId],
	(matchesSelector) => (playerSquadId: number, roundId: number) => {
		const matches = matchesSelector(roundId);
		return filter(matches, (it) => includes([it.awaySquadId, it.homeSquadId], playerSquadId));
	}
);

export const getNextScheduledMatchForPlayerSquad = createSelector([getUnits], (matches) =>
	memoize((playerSquadId: number) =>
		matches.find(
			(it) => isUnitScheduled(it) && includes([it.awaySquadId, it.homeSquadId], playerSquadId)
		)
	)
);

export const getScheduledUnitsByRoundId = createSelector(
	[getMatchesByRoundId],
	(matchesSelector) => (roundId: number) => {
		return matchesSelector(roundId).filter(isUnitScheduled);
	}
);

type NextMatchTypeSelector = [
	number,
	number,
	number,
	ReturnType<ReturnType<typeof getPredictionsByRoundId>>
];

export const nextMatchForDayByPlayer = createSelector(
	[getPlayerMatchesSelector],
	(matchesSelector) =>
		(...payload: NextMatchTypeSelector) => {
			const [playerId, squadId, activeRoundId, picks] = payload;
			const matches = matchesSelector(squadId, activeRoundId);

			return matches.find((it) => {
				const matchPickedForThatPlayer = picks.find(
					(prediction) => prediction.unitId === it.id && playerId === prediction.playerId
				);

				if (matchPickedForThatPlayer) {
					return false;
				}
				return isUnitScheduled(it);
			});
		}
);

export const getScheduledPlayerMatchesSelector = createSelector(
	[getPlayerMatchesSelector],
	(matchesSelector) => (playerSquadId: number, roundId: number) => {
		const matches = matchesSelector(playerSquadId, roundId);
		return filter(matches, isUnitScheduled);
	}
);

export const isHistoryMatchesLoaded = createSelector(
	[getUnitsStore],
	(units) => units.historyRequestState === RequestState.Received
);
